import {
    NATIVE_INVOICE_PAGE_SIZE_DIMENSIONS,
    NATIVE_INVOICE_PAGE_SIZE_LABELS,
    type NativeInvoiceLabelKey,
    type NativeInvoicePageSize,
} from "@common/constants/nativeInvoice";
import type { NativeInvoicePaymentMethod } from "@common/constants/nativeInvoicePaymentMethod";
import {
    NativeInvoiceCustomTermValue,
    NativeInvoiceTermType,
} from "@common/helpers/invoice-terms.helper";
import type { NativeInvoicePdfFieldSettings } from "@common/types/nativeInvoice";
import type { NativeInvoiceAdjustmentSettingsForm } from "./nativeInvoiceAdjustmentCoding";

// Invoice-local To party. Address/email live on the document, not on the
// deprecated Counterparty contact/address scalars.
export interface NativeInvoiceCustomer {
    id: string;
    name: string;
    email: string | null;
    // Counterparty default CC/BCC (comma-separated), carried so a customer
    // switch can drop the previous customer's defaults from the email fields.
    cc?: string | null;
    bcc?: string | null;
    phoneNumber: string | null;
    street?: string | null;
    street2?: string | null;
    city?: string | null;
    state?: string | null;
    postalCode?: string | null;
    country?: string | null;
}

// Ad-hoc line item a caller can prefill a create draft with: just a name and
// an amount, no catalog item required. Always persists with catalogItemId:
// null (see buildNativeInvoiceDraftBody / assertCatalogItemsValid, which both
// skip catalog validation entirely for lines with no catalogItemId).
export interface NativeInvoiceCreatePrefillLine {
    productOrService: string;
    amount: number;
    // Backlink to the billable-expense transaction this line was prefilled
    // from (see buildBillableExpenseInvoicePrefill). Present only for the
    // "create invoice from these expenses" flow; drives quantity/rate
    // read-only-ness and lets the backend exclude a removed row's transaction
    // from linking on save.
    billableExpenseTransactionId?: number;
}

// Prefill payload for opening the native invoice drawer directly into a
// pre-filled create draft (e.g. "create invoice from these billable
// expenses"). Lines are ad-hoc (name + amount); the rest mirrors a subset of
// NativeInvoiceFormState so a caller can seed the customer/dates/notes in one
// shot. Any field omitted here falls back to the normal create-mode default.
export interface NativeInvoiceCreatePrefill {
    lines: NativeInvoiceCreatePrefillLine[];
    customer?: NativeInvoiceCustomer | null;
    toCompany?: string;
    toFullName?: string;
    toTaxId?: string;
    invoiceDate?: string;
    dueDate?: string;
    poNumber?: string;
    note?: string;
    terms?: string;
    entityId?: number;
    billableExpenseTransactionIds?: number[];
}

export interface NativeInvoiceLineRow {
    id: string;
    // Optional backlink to a catalog item/price selected via the picker. The
    // snapshot still stores resolved name/amount, so render is independent of
    // the live item row.
    catalogItemId: string | null;
    catalogItemPriceId: string | null;
    productOrService: string;
    description: string;
    quantity: number | null;
    unitAmount: number | null;
    // Class + Accrual + Receivables coding (PRO-10698). Kept out of the render
    // snapshot (see nativeInvoiceLineAccountingSchema): these drive the
    // separate `lineAccounting` save payload, not the PDF/preview. Income
    // account targets the primary accrual ledger when the entity has one,
    // otherwise the primary cash ledger. Receivables still targets the
    // primary accrual ledger. Multi-ledger coding is out of scope here.
    classIds: string[];
    accrualAccountId: string | null;
    receivablesAccountId: string | null;
    // Labeled per-line Tax/Discount/Deposit inputs. Used when the matching
    // `is*PerLineItem` flag is on; otherwise the header row is the source of truth
    // and these stay at 0. Percent rows are a share of this line's amount.
    tax: number;
    discount: number;
    deposit: number;
    // Non-null when this line was prefilled from a billable-expense
    // transaction (see NativeInvoiceCreatePrefillLine). Quantity/rate are
    // read-only for such a line — see buildLineItemColumns — and the id is
    // carried through to the save payload so the backend can exclude the
    // transaction from linking if the row is removed before save.
    billableExpenseTransactionId: number | null;
}

// Persisted Class / Accrual / Receivables coding for a snapshot line, keyed by
// the snapshot line id (invoice line `sourceId`). Supplied separately from the
// render snapshot because coding is an accounting concern, not presentational.
// Cross-cutting type consumed by hydrate, save, and the accounting builder.
export type NativeInvoiceLineCodingByLineId = Readonly<
    Record<
        string,
        {
            classIds?: string[];
            accrualAccountId?: string | null;
            receivablesAccountId?: string | null;
        }
    >
>;

export type { NativeInvoicePageSize };

export type NativeInvoiceFooterAlign = "left" | "center" | "right";

// Footer text/alignment a freshly-opened create draft starts with. A template
// can override either; the empty-check compares against these to tell an
// untouched draft apart from one carrying template footer customization.
export const DEFAULT_NATIVE_INVOICE_FOOTER = "";

export const DEFAULT_NATIVE_INVOICE_FOOTER_ALIGN: NativeInvoiceFooterAlign =
    "left";

// Whether a Tax/Discount/Deposit row's entered value is a percentage of the
// subtotal or a fixed monetary amount. Defaults to "percent".
export type NativeInvoiceAdjustmentMode = "percent" | "amount";

export const DEFAULT_NATIVE_INVOICE_ADJUSTMENT_MODE: NativeInvoiceAdjustmentMode =
    "percent";

// Whether a Tax/Discount/Deposit row adds to ("positive") or subtracts from
// ("negative") the invoice total. The form derives this from the typed number
// (a minus flips the field's default: Tax adds, Discount and Deposit subtract).
export type NativeInvoiceAdjustmentSign = "positive" | "negative";

export const DEFAULT_NATIVE_INVOICE_TAX_SIGN: NativeInvoiceAdjustmentSign =
    "positive";
export const DEFAULT_NATIVE_INVOICE_DISCOUNT_SIGN: NativeInvoiceAdjustmentSign =
    "negative";
export const DEFAULT_NATIVE_INVOICE_DEPOSIT_SIGN: NativeInvoiceAdjustmentSign =
    "negative";

// New drafts start on US Letter. Legacy snapshots saved before the field
// existed keep rendering on NATIVE_INVOICE_DEFAULT_PAGE_SIZE (a4) — see
// pageSizeFromSnapshot in hydrateNativeInvoiceFormFromSnapshot.
export const DEFAULT_NATIVE_INVOICE_PAGE_SIZE: NativeInvoicePageSize = "letter";

export { NATIVE_INVOICE_PAGE_SIZE_DIMENSIONS, NATIVE_INVOICE_PAGE_SIZE_LABELS };

export interface NativeInvoiceTotals {
    subtotal: number;
    // Kind-native signed computed amounts (persist as snapshot.tax/discount/
    // deposit and feed split/JE rows). A minus is a flip of that row's default.
    taxAmount: number;
    discountAmount: number;
    depositAmount: number;
    // Derived from the labeled form values for PDF/preview display only.
    taxSign: NativeInvoiceAdjustmentSign;
    discountSign: NativeInvoiceAdjustmentSign;
    depositSign: NativeInvoiceAdjustmentSign;
    total: number;
}

export interface NativeInvoiceFromState {
    businessName: string;
    // Contact-person name on the sender party (distinct from the business /
    // entity name). Maps to the party `name` field in the render snapshot.
    name: string;
    email: string;
    phoneNumber: string;
    taxId: string;
    street: string;
    street2: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
}

export interface NativeInvoiceFormState {
    from: NativeInvoiceFromState;
    customer: NativeInvoiceCustomer | null;
    // Tax ID/EIN for the customer. Lives on form state (not on `customer`)
    // because NativeInvoiceCustomer has no taxId.
    toTaxId: string;
    // Contact-person name for the customer party (distinct from the company /
    // counterparty name). Lives on form state because NativeInvoiceCustomer
    // has no separate contact-person field. Maps to the To party `name` field
    // in the render snapshot.
    toFullName: string;
    toCompany: string;
    // Editable "Reply to" recipient, split into a display name and an email
    // address. The email is what's sent as the email snapshot's `replyTo`; both
    // default to the current user so replies reach the sender unless overridden.
    replyToName: string;
    replyToEmail: string;
    invoiceNumber: string;
    invoiceDate: string;
    dueDate: string;
    poNumber: string;
    note: string;
    // Free-text "Payment instructions" field, printed on the invoice (see
    // renderNativeInvoiceDocument). Distinct from the
    // structured `termType`/`customTerm` below, which never appear on the
    // invoice — they only drive due-date auto-population at creation time.
    terms: string;
    // Structured Terms dropdown selection (PRO-11287): On Receipt / Net N /
    // Custom. Persisted on native invoice detail so a later apply/occurrence
    // can derive a new due date. Not printed on the invoice. Null means no
    // term has been selected yet.
    termType: NativeInvoiceTermType | null;
    // Populated only when termType === CUSTOM.
    customTerm: NativeInvoiceCustomTermValue | null;
    footer: string;
    footerAlign: NativeInvoiceFooterAlign;
    // Page-count indicator ("1 of 2") on the footer's right side. On by
    // default; the Design section's Pagination toggle turns it off.
    showPagination: boolean;
    // Labeled values the user entered (may be negative). A positive Tax adds;
    // a positive Discount or Deposit subtracts. Snapshot `*Sign` fields are
    // derived at save from these — they are not stored on the form.
    tax: number;
    discount: number;
    deposit: number;
    taxMode: NativeInvoiceAdjustmentMode;
    discountMode: NativeInvoiceAdjustmentMode;
    depositMode: NativeInvoiceAdjustmentMode;
    // When true, the header Tax/Discount/Deposit input is read-only and each
    // line has its own column. The header %/$ toggle still applies to every
    // line. Header totals are the sum of the per-line computed amounts.
    isTaxPerLineItem: boolean;
    isDiscountPerLineItem: boolean;
    isDepositPerLineItem: boolean;
    // Whether the per-line value is also rendered as its own column in the
    // PDF's line-items table, distinct from isXPerLineItem above (which
    // controls where the value is CALCULATED). Only meaningful — and only
    // editable in the UI — while the matching isXPerLineItem is also true.
    isTaxDisplayedPerLineItem: boolean;
    isDiscountDisplayedPerLineItem: boolean;
    isDepositDisplayedPerLineItem: boolean;
    // Account, receivables account, and classes for the synthetic tax /
    // discount / deposit lines. Edited in the Items settings section and
    // copied onto those lines when the invoice is saved.
    adjustmentSettings: NativeInvoiceAdjustmentSettingsForm;
    pageSize: NativeInvoicePageSize;
    // Design step (PRO-9866) branding fields. `logoPath` is the persisted S3
    // object key; `logoPreviewUrl` is a transient display URL (from the upload
    // endpoint) used only by the live preview and never persisted.
    logoPath: string | null;
    logoPreviewUrl: string | null;
    backgroundColor: string | null;
    accentColor: string | null;
    textColor: string | null;
    fontFamily: string | null;
    // Persisted PDF field-label overrides. Omitted keys fall back to
    // NATIVE_INVOICE_LABEL_DEFAULTS at preview and print time. Null means
    // every label uses the default. Most labels are editable via the Fields &
    // visibility panel (see InvoiceFieldSettingsPanel) which writes through
    // `fieldSettings` instead of this map directly (see customLabelOverrides
    // in applyNativeInvoicePdfFieldSettings) — a few (e.g. taxId,
    // payThisInvoice) still only round-trip via a snapshot set outside the UI.
    labelOverrides: Partial<Record<NativeInvoiceLabelKey, string>> | null;
    // Fields & visibility panel state, including the Header section (top-left
    // "INVOICE" heading override/hide - see fieldSettings.header).
    fieldSettings: NativeInvoicePdfFieldSettings;
    lines: NativeInvoiceLineRow[];
    emailSubject: string;
    emailMessage: string;
    isEmailSubjectAutomatic: boolean;
    isEmailMessageAutomatic: boolean;
    // Additional To/Cc/Bcc recipient lists are included in the email snapshot
    // whenever they
    // contain a valid address; there is no separate on/off toggle.
    emailTo: string;
    emailCc: string;
    emailBcc: string;
    emailAttachPdf: boolean;
    // Drives which primary action the details command bar offers ("Create &
    // send email" vs plain "Create"). UI-only, never persisted.
    sendEmailEnabled: boolean;
    scheduleEnabled: boolean;
    scheduledSendAt: string;
    // Null = Kick's invoices@kick.co. A uuid is a workspace sending email.
    sendingEmailId: string | null;
    // Opt-in Stripe Checkout. Off by default. When on, `stripeFinancialAccountId`
    // is the entity-scoped Stripe FA that collects the charge (required when the
    // entity has more than one Stripe FA).
    collectPayment: boolean;
    stripeFinancialAccountId: number | null;
    // Methods offered on the pay page. Only sent while `collectPayment` is on.
    acceptedPaymentMethods: NativeInvoicePaymentMethod[];
}

// Internal form state stores soft-default fields as overrides: null means
// "keep deriving from live props / entity". The public `NativeInvoiceFormState`
// always exposes resolved strings — no refs or catch-up effects needed.
export interface NativeInvoiceFormRawState extends Omit<
    NativeInvoiceFormState,
    | "emailSubject"
    | "emailMessage"
    | "isEmailSubjectAutomatic"
    | "isEmailMessageAutomatic"
    | "invoiceNumber"
    | "replyToName"
    | "replyToEmail"
> {
    invoiceNumberOverride: string | null;
    replyToNameOverride: string | null;
    replyToEmailOverride: string | null;
    fromEmailOverride: string | null;
    emailSubjectOverride: string | null;
    emailMessageOverride: string | null;
}

// The subset of form fields hydrate produces from a persisted render snapshot.
// Derived from `NativeInvoiceFormState` (single type surface) by omitting the
// fields hydrate does not populate: reply-to and the email/schedule block are
// seeded by the create/edit factory, not the snapshot.
export type NativeInvoiceHydratedFields = Omit<
    NativeInvoiceFormState,
    | "replyToName"
    | "replyToEmail"
    | "emailSubject"
    | "emailMessage"
    | "isEmailSubjectAutomatic"
    | "isEmailMessageAutomatic"
    | "emailTo"
    | "emailCc"
    | "emailBcc"
    | "emailAttachPdf"
    | "sendEmailEnabled"
    | "scheduleEnabled"
    | "scheduledSendAt"
    | "termType"
    | "customTerm"
    | "sendingEmailId"
    | "collectPayment"
    | "stripeFinancialAccountId"
    | "acceptedPaymentMethods"
    | "adjustmentSettings"
>;
